<template>
    <div class="range-slider">
        <div class="slider-track">
            <div
                class="slider-range"
                :style="rangeStyle"
            ></div>
        </div>

        <!-- Left thumb (internal 0..100 range) -->
        <input
            type="range"
            class="thumb thumb-left"
            min="0"
            max="100"
            step="0.1"
            :value="innerMinSlider"
            @input="onMinSliderInput"
            @change="onMinSliderChangeFinished"
        />

        <!-- Right thumb (internal 0..100 range) -->
        <input
            type="range"
            class="thumb thumb-right"
            min="0"
            max="100"
            step="0.1"
            :value="innerMaxSlider"
            @input="onMaxSliderInput"
            @change="onMaxSliderChangeFinished"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

/**
 * Props:
 * - modelValue: Array [realMin, realMax] (the actual values).
 * - min, max: real boundaries, e.g. 0..402.
 * - factor: controls how "spread" small values are. Example ~0.26 for 0..402 to put 1 ~5%.
 */
const props = defineProps({
    modelValue: {
        type: Array,
        default: () => [0, 100]
    },
    min: {
        type: Number,
        default: 0
    },
    max: {
        type: Number,
        default: 100
    },
    factor: {
        type: Number,
        default: 1 // adjust or solve for your domain
    }
})

const emit = defineEmits(['update:modelValue', 'update:end'])

/**
 * Internal sliders: 0..100.
 * We'll map real values -> these internal positions and vice versa.
 */
const innerMinSlider = ref(0)
const innerMaxSlider = ref(100)

/**
 * Convert real value -> internal slider in [0..100],
 * using a "log-like" scale with 'factor'.
 * If factor is small, the scale is closer to linear.
 * If factor is large, small values get more slider space.
 */
function toSliderValue(realVal) {
    const range = props.max - props.min
    if (range <= 0) {
        // fallback if min >= max
        return 0
    }

    let v = realVal
    if (v < props.min) v = props.min
    if (v > props.max) v = props.max

    // shift the domain so 0 => (v - props.min)
    const shifted = v - props.min
    // slider ratio = ln(1 + factor * shifted ) / ln(1 + factor * range )
    const numerator = Math.log(1 + props.factor * shifted)
    const denominator = Math.log(1 + props.factor * range)
    if (denominator === 0) return 0

    const ratio = numerator / denominator
    return ratio * 100
}

/**
 * Convert internal slider (0..100) -> real value,
 * using the inverse of the formula above.
 */
function fromSliderValue(sliderVal) {
    const range = props.max - props.min
    if (range <= 0) {
        return props.min
    }

    const ratio = sliderVal / 100
    const exponent = ratio * Math.log(1 + props.factor * range)
    // => realVal = min + (1/factor)*[exp(exponent) - 1]
    const shifted = Math.exp(exponent) - 1
    const realVal = props.min + (shifted / props.factor)
    return realVal
}

/**
 * Initialize from props.modelValue => internal [0..100].
 */
watch(
    () => props.modelValue,
    (newVal) => {
        if (Array.isArray(newVal) && newVal.length === 2) {
            innerMinSlider.value = toSliderValue(newVal[0])
            innerMaxSlider.value = toSliderValue(newVal[1])
        }
    },
    { immediate: true }
)

/**
 * If props.min, props.max, or factor changes,
 * re-map the current internal slider to the new scale.
 */
watch(
    () => [props.min, props.max, props.factor],
    () => {
        const currentRealMin = fromSliderValue(innerMinSlider.value)
        const currentRealMax = fromSliderValue(innerMaxSlider.value)
        innerMinSlider.value = toSliderValue(currentRealMin)
        innerMaxSlider.value = toSliderValue(currentRealMax)
    }
)

/**
 * Whenever the internal slider changes, emit new real values.
 */
watch([innerMinSlider, innerMaxSlider], () => {
    const realMin = fromSliderValue(innerMinSlider.value)
    const realMax = fromSliderValue(innerMaxSlider.value)
    emit('update:modelValue', [realMin, realMax])
})

/**
 * Input handlers (avoid min slider surpassing max slider).
 */
function onMinSliderInput(e) {
    const val = Number(e.target.value)
    // If new min goes beyond current max, swap them
    if (val > innerMaxSlider.value) {
        const oldMax = innerMaxSlider.value
        innerMaxSlider.value = val
        innerMinSlider.value = oldMax
    } else {
        innerMinSlider.value = val
    }
}

function onMaxSliderInput(e) {
    const val = Number(e.target.value)
    // If new max goes below current min, swap them
    if (val < innerMinSlider.value) {
        const oldMin = innerMinSlider.value
        innerMinSlider.value = val
        innerMaxSlider.value = oldMin
    } else {
        innerMaxSlider.value = val
    }
}

function onMinSliderChangeFinished(e) {
    // Called only when the user lets go (mouse up)
    // If you want to do something “final” here (like telling the parent “done editing”),
    // you can emit some event or call updateValue in the parent, etc.
    emitFinalChange()
}

function onMaxSliderChangeFinished(e) {
    // Called only when the user lets go (mouse up)
    // If you want to do something “final” here (like telling the parent “done editing”),
    // you can emit some event or call updateValue in the parent, etc.
    emitFinalChange()
}


function emitFinalChange() {
    // example method that calls 'update:modelValue' or some other event
    // possibly the same logic you had in 'updateValue()'
    const realMin = fromSliderValue(innerMinSlider.value)
    const realMax = fromSliderValue(innerMaxSlider.value)
    emit('update:end', [realMin, realMax])
}


/**
 * Computed style for the highlighted track area.
 */
const rangeStyle = computed(() => {
    const leftP = Math.min(innerMinSlider.value, innerMaxSlider.value)
    const rightP = 100 - Math.max(innerMinSlider.value, innerMaxSlider.value)
    return {
        left: `${leftP}%`,
        right: `${rightP}%`
    }
})
</script>

